import React, { useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  LinearProgress,
  Alert,
  AlertTitle,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  InsertDriveFile as FileIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import csvService from '../services/csvService';
import { useNotification } from '../contexts/NotificationContext';

const CSVUploadComponent = () => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [processingTasks, setProcessingTasks] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const { showSuccess, showError, showInfo } = useNotification();

  // Configuração do dropzone
  const onDrop = useCallback(async (acceptedFiles, rejectedFiles) => {
    // Processar arquivos rejeitados
    if (rejectedFiles.length > 0) {
      rejectedFiles.forEach(rejection => {
        const errors = rejection.errors.map(e => e.message).join(', ');
        showError(`Arquivo ${rejection.file.name}: ${errors}`);
      });
    }

    // Validar e processar arquivos aceitos
    const validFiles = [];
    
    for (const file of acceptedFiles) {
      const validation = csvService.validateCsvFile(file);
      
      if (validation.valid) {
        validFiles.push(file);
      } else {
        showError(`${file.name}: ${validation.errors.join(', ')}`);
      }
    }

    if (validFiles.length === 0) return;

    // Adicionar arquivos à lista de upload
    const newFiles = validFiles.map(file => ({
      id: Date.now() + Math.random(),
      file,
      name: file.name,
      size: file.size,
      status: 'ready',
      progress: 0
    }));

    setUploadedFiles(prev => [...prev, ...newFiles]);

    // Iniciar upload automaticamente
    if (validFiles.length === 1) {
      await handleSingleUpload(validFiles[0], newFiles[0].id);
    } else {
      await handleMultipleUpload(validFiles, newFiles.map(f => f.id));
    }
  }, [showError]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.csv']
    },
    maxFiles: 5,
    maxSize: 500 * 1024 * 1024, // 500MB
    multiple: true
  });

  // Upload de arquivo único
  const handleSingleUpload = async (file, fileId) => {
    try {
      updateFileStatus(fileId, 'uploading', 0);
      
      const response = await csvService.uploadCsv(file);
      
      updateFileStatus(fileId, 'processing', 0);
      
      // Adicionar à lista de tarefas de processamento
      const task = {
        id: response.task_id,
        fileId,
        filename: response.filename,
        totalRows: response.total_rows,
        status: response.status,
        progress: 0,
        startTime: new Date(),
        message: response.message
      };
      
      setProcessingTasks(prev => [...prev, task]);
      
      // Monitorar progresso
      csvService.monitorProcessing(
        response.task_id,
        (status) => updateTaskProgress(response.task_id, status),
        (result) => handleProcessingComplete(response.task_id, result),
        (error) => handleProcessingError(response.task_id, error)
      );
      
      showSuccess(`Upload iniciado: ${file.name}`);
      
    } catch (error) {
      updateFileStatus(fileId, 'error', 0);
      showError(`Erro no upload de ${file.name}: ${error.message}`);
    }
  };

  // Upload de múltiplos arquivos
  const handleMultipleUpload = async (files, fileIds) => {
    try {
      fileIds.forEach(id => updateFileStatus(id, 'uploading', 0));
      
      const response = await csvService.uploadMultipleCsv(files);
      
      fileIds.forEach(id => updateFileStatus(id, 'processing', 0));
      
      // Adicionar todas as tarefas
      const newTasks = response.map((res, index) => ({
        id: res.task_id,
        fileId: fileIds[index],
        filename: res.filename,
        totalRows: res.total_rows,
        status: res.status,
        progress: 0,
        startTime: new Date(),
        message: res.message
      }));
      
      setProcessingTasks(prev => [...prev, ...newTasks]);
      
      // Monitorar cada tarefa
      newTasks.forEach(task => {
        if (task.status === 'processing') {
          csvService.monitorProcessing(
            task.id,
            (status) => updateTaskProgress(task.id, status),
            (result) => handleProcessingComplete(task.id, result),
            (error) => handleProcessingError(task.id, error)
          );
        }
      });
      
      showSuccess(`Upload iniciado para ${files.length} arquivos`);
      
    } catch (error) {
      fileIds.forEach(id => updateFileStatus(id, 'error', 0));
      showError(`Erro no upload múltiplo: ${error.message}`);
    }
  };

  // Atualizar status do arquivo
  const updateFileStatus = (fileId, status, progress) => {
    setUploadedFiles(prev => prev.map(file => 
      file.id === fileId ? { ...file, status, progress } : file
    ));
  };

  // Atualizar progresso da tarefa
  const updateTaskProgress = (taskId, status) => {
    setProcessingTasks(prev => prev.map(task => 
      task.id === taskId ? {
        ...task,
        status: status.status,
        progress: status.progress,
        message: status.message,
        processedRows: status.processed_rows,
        successfulRows: status.successful_rows,
        failedRows: status.failed_rows,
        duplicateRows: status.duplicate_rows,
        skippedRows: status.skipped_rows,
        errorsCount: status.errors_count,
        estimatedRemaining: status.estimated_remaining
      } : task
    ));

    // Atualizar arquivo correspondente
    const task = processingTasks.find(t => t.id === taskId);
    if (task) {
      updateFileStatus(task.fileId, status.status, status.progress);
    }
  };

  // Processamento concluído
  const handleProcessingComplete = (taskId, result) => {
    setProcessingTasks(prev => prev.map(task => 
      task.id === taskId ? {
        ...task,
        status: 'completed',
        progress: 100,
        result,
        skippedRows: result.skipped_rows,
        endTime: new Date()
      } : task
    ));

    const task = processingTasks.find(t => t.id === taskId);
    if (task) {
      updateFileStatus(task.fileId, 'completed', 100);
      showSuccess(`Processamento concluído: ${task.filename}`);
    }
  };

  // Erro no processamento
  const handleProcessingError = (taskId, error) => {
    setProcessingTasks(prev => prev.map(task => 
      task.id === taskId ? {
        ...task,
        status: 'failed',
        message: error.message,
        endTime: new Date()
      } : task
    ));

    const task = processingTasks.find(t => t.id === taskId);
    if (task) {
      updateFileStatus(task.fileId, 'failed', 0);
      showError(`Erro no processamento: ${task.filename}`);
    }
  };

  // Remover arquivo da lista
  const removeFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
    setProcessingTasks(prev => prev.filter(task => task.fileId !== fileId));
  };

  // Mostrar detalhes do resultado
  const showResultDetails = (task) => {
    setSelectedResult(task);
    setShowResults(true);
  };

  // Obter ícone do status
  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckIcon color="success" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      case 'processing':
      case 'uploading':
        return <InfoIcon color="info" />;
      default:
        return <FileIcon />;
    }
  };

  // Obter cor do status
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'processing':
      case 'uploading':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Upload de Arquivos CSV
      </Typography>
      
      {/* Área de Drop */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            {...getRootProps()}
            sx={{
              border: '2px dashed',
              borderColor: isDragActive ? 'primary.main' : 'grey.300',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              bgcolor: isDragActive ? 'action.hover' : 'transparent',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'action.hover'
              }
            }}
          >
            <input {...getInputProps()} />
            <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {isDragActive ? 'Solte os arquivos aqui' : 'Arraste arquivos CSV ou clique para selecionar'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Máximo de 1 arquivo, 500MB cada. Formatos aceitos: .csv
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Lista de Arquivos */}
      {uploadedFiles.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Arquivos ({uploadedFiles.length})
            </Typography>
            
            <List>
              {uploadedFiles.map((file, index) => (
                <React.Fragment key={file.id}>
                  <ListItem
                    secondaryAction={
                      <Tooltip title="Remover arquivo">
                        <IconButton onClick={() => removeFile(file.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    }
                  >
                    <ListItemIcon>
                      {getStatusIcon(file.status)}
                    </ListItemIcon>
                    <ListItemText
                      primary={file.name}
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {csvService.formatFileSize(file.size)}
                          </Typography>
                          {file.status === 'processing' || file.status === 'uploading' ? (
                            <Box sx={{ mt: 1 }}>
                              <LinearProgress 
                                variant="determinate" 
                                value={file.progress} 
                                sx={{ mb: 0.5 }}
                              />
                              <Typography variant="caption">
                                {file.progress.toFixed(1)}%
                              </Typography>
                            </Box>
                          ) : (
                            <Chip 
                              label={csvService.formatStatus(file.status)} 
                              color={getStatusColor(file.status)}
                              size="small"
                              sx={{ mt: 0.5 }}
                            />
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < uploadedFiles.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Tarefas de Processamento */}
      {processingTasks.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Status do Processamento ({processingTasks.length})
            </Typography>
            
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Arquivo</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Progresso</TableCell>
                    <TableCell align="center">Linhas</TableCell>
                    <TableCell align="center">Skipped</TableCell>
                    <TableCell align="center">Tempo</TableCell>
                    <TableCell align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {processingTasks.map((task) => (
                    <TableRow key={task.id}>
                      <TableCell>
                        <Typography variant="body2" noWrap>
                          {task.filename}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {task.totalRows.toLocaleString()} linhas
                        </Typography>
                      </TableCell>
                      
                      <TableCell align="center">
                        <Chip 
                          label={csvService.formatStatus(task.status)}
                          color={getStatusColor(task.status)}
                          size="small"
                        />
                      </TableCell>
                      
                      <TableCell align="center">
                        {task.status === 'processing' ? (
                          <Box sx={{ minWidth: 100 }}>
                            <LinearProgress 
                              variant="determinate" 
                              value={task.progress} 
                              sx={{ mb: 0.5 }}
                            />
                            <Typography variant="caption">
                              {task.progress.toFixed(1)}%
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2">
                            {task.status === 'completed' ? '100%' : '-'}
                          </Typography>
                        )}
                      </TableCell>
                      
                      <TableCell align="center">
                        {task.processedRows !== undefined ? (
                          <Box>
                            <Typography variant="body2">
                              {task.processedRows?.toLocaleString() || 0}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ✓ {task.successfulRows || 0} | ✗ {task.failedRows || 0} | ⚠ {task.duplicateRows || 0}
                            </Typography>
                          </Box>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">
                          {task.skippedRows !== undefined ? task.skippedRows : '-'}
                        </Typography>
                      </TableCell>
                      
                      <TableCell align="center">
                        <Typography variant="body2">
                          {task.estimatedRemaining !== undefined && task.estimatedRemaining > 0 ? (
                            `~${csvService.formatTime(task.estimatedRemaining)}`
                          ) : task.endTime ? (
                            csvService.formatTime(
                              Math.floor((task.endTime - task.startTime) / 1000)
                            )
                          ) : (
                            '-'
                          )}
                        </Typography>
                      </TableCell>
                      
                      <TableCell align="center">
                        {task.status === 'completed' && task.result && (
                          <Button
                            size="small"
                            onClick={() => showResultDetails(task)}
                          >
                            Ver Detalhes
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Dialog de Resultados */}
      <Dialog 
        open={showResults} 
        onClose={() => setShowResults(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Resultado do Processamento
          <IconButton
            aria-label="close"
            onClick={() => setShowResults(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent>
          {selectedResult && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Alert severity={selectedResult.status === 'completed' ? 'success' : 'error'}>
                  <AlertTitle>
                    {selectedResult.filename} - {csvService.formatStatus(selectedResult.status)}
                  </AlertTitle>
                  Processamento {selectedResult.status === 'completed' ? 'concluído' : 'falhou'} em{' '}
                  {csvService.formatTime(selectedResult.result?.processing_time || 0)}
                </Alert>
              </Grid>
              
              {selectedResult.result && (
                <>
                  <Grid item xs={6}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h6" gutterBottom>Estatísticas</Typography>
                      <Typography>Total de linhas: {selectedResult.result.total_rows.toLocaleString()}</Typography>
                      <Typography color="success.main">
                        Processadas com sucesso: {selectedResult.result.successful_rows.toLocaleString()}
                      </Typography>
                      <Typography color="error.main">
                        Com erro: {selectedResult.result.failed_rows.toLocaleString()}
                      </Typography>
                      <Typography color="warning.main">
                        Duplicadas: {selectedResult.result.duplicate_rows.toLocaleString()}
                      </Typography>
                      {selectedResult.result.skipped_rows !== undefined && (
                        <Typography color="text.secondary">
                          Puladas: {selectedResult.result.skipped_rows.toLocaleString()}
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h6" gutterBottom>Vendas</Typography>
                      {selectedResult.result.total_revenue && (
                        <Typography>
                          Receita total: R$ {selectedResult.result.total_revenue.toLocaleString('pt-BR', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                        </Typography>
                      )}
                      {selectedResult.result.paid_orders && (
                        <Typography>
                          Pedidos pagos: {selectedResult.result.paid_orders.toLocaleString()}
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                  
                  {selectedResult.result.errors && selectedResult.result.errors.length > 0 && (
                    <Grid item xs={12}>
                      <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom color="error">
                          Erros Encontrados ({selectedResult.result.errors.length})
                        </Typography>
                        <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                          {selectedResult.result.errors.slice(0, 10).map((error, index) => (
                            <Alert severity="error" key={index} sx={{ mb: 1 }}>
                              <Typography variant="body2">
                                <strong>Linha {error.row}:</strong> {error.error}
                              </Typography>
                            </Alert>
                          ))}
                          {selectedResult.result.errors.length > 10 && (
                            <Typography variant="caption" color="text.secondary">
                              ... e mais {selectedResult.result.errors.length - 10} erros
                            </Typography>
                          )}
                        </Box>
                      </Paper>
                    </Grid>
                  )}
                  {selectedResult.result.skipped_reasons_summary && Object.keys(selectedResult.result.skipped_reasons_summary).length > 0 && (
                    <Grid item xs={12}>
                      <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                          Motivos de Linhas Puladas
                        </Typography>
                        {Object.entries(selectedResult.result.skipped_reasons_summary).map(([reason, count]) => (
                          <Typography key={reason} variant="body2">
                            {reason}: {count}
                          </Typography>
                        ))}
                      </Paper>
                    </Grid>
                  )}
                  {selectedResult.result.errors_summary && Object.keys(selectedResult.result.errors_summary).length > 0 && (
                    <Grid item xs={12}>
                      <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                          Resumo de Erros
                        </Typography>
                        {Object.entries(selectedResult.result.errors_summary).map(([err, count]) => (
                          <Typography key={err} variant="body2">
                            {err}: {count}
                          </Typography>
                        ))}
                      </Paper>
                    </Grid>
                  )}
                </>
              )}
            </Grid>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setShowResults(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CSVUploadComponent;